import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { KeyRound } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { changePassword } from '@/api/auth-api'
import { useAuth } from '@/auth/use-auth'
import { ProblemAlert } from '@/components/error-surface'
import { PasswordField } from '@/components/form/fields'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

import { AuthLayout } from './auth-layout'

/**
 * Twelve characters is the institution's floor, and there is no forced rotation: a policy that
 * makes people cycle through Password1! … Password9! buys nothing. The server holds the
 * authority — it checks length, the breached-password deny list, and reuse — so this schema only
 * catches what can be caught before a round trip.
 */
const MINIMUM_LENGTH = 12

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: z.string().min(MINIMUM_LENGTH, `Use at least ${MINIMUM_LENGTH} characters.`),
    confirmPassword: z.string().min(1, 'Repeat the new password.'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The two entries do not match.',
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    path: ['newPassword'],
    message: 'Choose a password you have not used before.',
  })

type ChangePasswordValues = z.infer<typeof changePasswordSchema>

export function ChangePasswordPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/password/change' })
  const challenge = auth.challenge?.kind === 'password_change' ? auth.challenge : null

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })

  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (outcome) => {
      auth.applyOutcome(outcome, challenge?.username ?? auth.user?.username ?? '')

      if (outcome.outcome === 'mfa_required') {
        void navigate({ to: '/mfa', search })
      } else if (outcome.outcome === 'authenticated') {
        void (search.redirect ? navigate({ href: search.redirect }) : navigate({ to: '/' }))
      }
    },
  })

  return (
    <AuthLayout
      title="Set a new password"
      description={
        challenge ? 'Your password must be changed before you can continue.' : 'Choose a new password.'
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          void form.handleSubmit((values) => {
            mutation.mutate({
              challengeToken: challenge?.token,
              currentPassword: values.currentPassword,
              newPassword: values.newPassword,
            })
          })(event)
        }}
        noValidate
      >
        {challenge?.reason ? (
          <Alert tone="warning">
            <AlertDescription>{challenge.reason}</AlertDescription>
          </Alert>
        ) : null}

        <PasswordField
          control={form.control}
          name="currentPassword"
          label="Current password"
          autoComplete="current-password"
          autoFocus
          required
        />

        <PasswordField
          control={form.control}
          name="newPassword"
          label="New password"
          autoComplete="new-password"
          description={`At least ${MINIMUM_LENGTH} characters. A passphrase of three unrelated words is both stronger and easier to key at a counter.`}
          required
        />

        <PasswordField
          control={form.control}
          name="confirmPassword"
          label="Repeat new password"
          autoComplete="new-password"
          required
        />

        {mutation.isError ? <ProblemAlert error={mutation.error} /> : null}

        <Button type="submit" variant="primary" className="w-full" disabled={mutation.isPending}>
          <KeyRound />
          {mutation.isPending ? 'Saving…' : 'Set password'}
        </Button>
      </form>
    </AuthLayout>
  )
}
